// Form validation helpers — submit button state and checkbox group required.
//
// Submit button: disables until all required, non-disabled fields are filled.
// Handles text, select, radio groups, and checkbox groups correctly.
//
// Checkbox group required: HTML `required` on a single checkbox means "this
// specific box must be checked", not "at least one in the group". We toggle
// the required attribute dynamically so the browser's native validation
// enforces the "at least one checked" semantic for the whole group.
//
// Asterisks on required labels are rendered server-side via the req_label
// helper in application_helper.rb.

////////////////////////////////////////////////////////////////////////////////
// Submit button state
////////////////////////////////////////////////////////////////////////////////

const formControllers = new WeakMap();

function isFieldFilled(field) {
  if (field.type === "checkbox" || field.type === "radio") {
    return Array.from(
      document.querySelectorAll(
        `input[type="${field.type}"][name="${CSS.escape(field.name)}"]`,
      ),
    ).some((el) => el.checked);
  }
  return field.value.trim() !== "";
}

function allRequiredFilled(form) {
  const seen = new Set();
  return Array.from(form.querySelectorAll("[required]:not([disabled])")).every(
    (field) => {
      // Radio and checkbox groups: avoid checking the same group multiple times.
      if (field.type === "checkbox" || field.type === "radio") {
        const key = `${field.type}:${field.name}`;
        if (seen.has(key)) return true;
        seen.add(key);
      }
      return isFieldFilled(field);
    },
  );
}

function updateButton(form) {
  const btn = form.querySelector('button[type="submit"]');
  if (!btn) return;
  btn.disabled = !allRequiredFilled(form);
}

function bindForm(form) {
  updateButton(form);

  const controller = new AbortController();
  formControllers.set(form, controller);

  form.addEventListener("input", () => updateButton(form), {
    signal: controller.signal,
  });
  form.addEventListener("change", () => updateButton(form), {
    signal: controller.signal,
  });
}

////////////////////////////////////////////////////////////////////////////////
// Checkbox group required
////////////////////////////////////////////////////////////////////////////////

// HTML `required` on a single checkbox means "this specific box must be
// checked", not "at least one in the group must be checked". We adjust the
// required attribute on all checkboxes in a group so the browser's native
// validation enforces "at least one" semantics.

const checkboxGroupControllers = new Map(); // group name → AbortController

function bindCheckboxGroup(name, checkboxes) {
  if (checkboxGroupControllers.has(name)) return;

  const controller = new AbortController();
  checkboxGroupControllers.set(name, controller);

  const update = () => {
    const anyChecked = checkboxes.some((cb) => cb.checked);
    checkboxes.forEach((cb) => {
      cb.required = !anyChecked;
    });
  };

  checkboxes.forEach((cb) => {
    cb.addEventListener("change", update, { signal: controller.signal });
  });
  update();
}

function bindCheckboxGroups() {
  const seen = new Set();
  document
    .querySelectorAll('input[type="checkbox"][required]')
    .forEach((cb) => {
      if (seen.has(cb.name)) return;
      seen.add(cb.name);
      const group = Array.from(
        document.querySelectorAll(
          `input[type="checkbox"][name="${CSS.escape(cb.name)}"]`,
        ),
      );
      bindCheckboxGroup(cb.name, group);
    });
}

function resetCheckboxGroups() {
  checkboxGroupControllers.forEach((ctrl) => ctrl.abort());
  checkboxGroupControllers.clear();
}

////////////////////////////////////////////////////////////////////////////////
// Turbo lifecycle
////////////////////////////////////////////////////////////////////////////////

function bindAll() {
  bindCheckboxGroups();

  document.querySelectorAll("form").forEach((form) => {
    if (formControllers.has(form)) return;
    if (!form.querySelector("[required]:not([disabled])")) return;
    if (!form.querySelector('button[type="submit"]')) return;
    bindForm(form);
  });
}

document.addEventListener("turbo:load", bindAll);

// On frame-load, new DOM replaces the frame content. String-keyed checkbox
// group controllers point to the old (replaced) elements — reset and rebind.
document.addEventListener("turbo:frame-load", () => {
  resetCheckboxGroups();
  bindAll();
});

document.addEventListener("turbo:before-stream-render", () => {
  setTimeout(bindAll, 0);
});

document.addEventListener("turbo:before-cache", () => {
  document.querySelectorAll("form").forEach((form) => {
    const ctrl = formControllers.get(form);
    if (ctrl) {
      ctrl.abort();
      formControllers.delete(form);
    }
  });

  resetCheckboxGroups();
});
